import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import CustomPopup from './CustomPopup';
import Button from '../../library/Button/button';
import IModuleList from '../../utils/ModuleList/ModuleList.interface';
import apiCallWrapper from '../../apiwrapper/apiCallWrapper';
import { ModuleAccessLevel } from '../../utils/Helper/enums';
import ConfirmationPopUp from '../../library/ConfirmationPopUp/ConfirmationPopUp';
import useNetworkConnection from '../../library/NetworkConnection/NetworkConnection';
import InputField from '../../library/inputField/inputField';
import { IPPFourthLevelHeading, IPPHeading, IPPParagraph } from '../../library/Heading/Heading';
import IUserList from '../../utils/UserList/UserList.interface';
import ConditionalLoader from '../ConditionalLoader';
import { useAppDispatch, useAppSelector } from '../../hooks/storeHooks';
import { changePartner, getAllAdmins } from '../../store/slice/partnersSlice';

/**
 * This common props takes in one parameter.
 * @organisationName - This prop is used for storing the organisation name.
 **/
interface AssignedModuleProps {
  organisationName: string | null;
  partner: IUserList;
  onChangePartner: (partner: IUserList) => void;
}

// This component is responsible for managing and displaying the assignment of access levels for various modules within an organization, allowing users to select access levels and submit the changes.
export const AssignedModuleForm: React.FC<AssignedModuleProps> = ({
  organisationName,
  partner,
  onChangePartner
}) => {
  interface IModuleAccessObject {
    [moduleName: string]: string;
  }
  const dispatch = useAppDispatch();
  const moduleState = useAppSelector((state) => state.modules);
  const presetState = useAppSelector((state) => state.presets);
  const partnersState = useAppSelector((state) => state.partners);
  const partnerFromRedux = partnersState.data?.find((p) => p.email === partner.email) ?? partner;
  const presets = presetState.data ?? [];
  const modules = moduleState.data ?? [];
  const systemModules = modules.filter((module) => module?.isSystemModule);
  const baseModules = modules.filter((module) => !module?.isSystemModule);
  const backEndServerUrl = String(process.env.REACT_APP_BACK_END_SERVER_URL ?? '');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showLoader, setShowLoader] = useState<boolean>(false);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [canReOpen, setCanReopen] = useState<boolean>(false);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [submissionInProgress, setSubmissionInProgress] = useState<boolean>(false);
  const [responseMessage, setResponseMessage] = useState<string>('Please Wait...');
  const [noteMessage, setNoteMessage] = useState<string | null>(null);

  const [selectedAccessTypes, setSelectedAccessTypes] = useState<IModuleAccessObject>({});
  const [activeTab, setActiveTab] = useState<string>('Base Modules');
  const [defaultAccessTypes, setDefaultAccessTypes] = useState<IModuleAccessObject>({});
  const [selectedPreset, setselectedPreset] = useState<number>(-1);

  // Use refs to store current values to avoid stale closures in useEffect
  const presetsRef = useRef(presets);
  const baseModulesRef = useRef(baseModules);
  const systemModulesRef = useRef(systemModules);

  // Update refs when values change
  useEffect(() => {
    presetsRef.current = presets;
  }, [presets]);

  useEffect(() => {
    baseModulesRef.current = baseModules;
  }, [baseModules]);

  useEffect(() => {
    systemModulesRef.current = systemModules;
  }, [systemModules]);

  // Reusable hook to check internet connectivity
  const { isConnected } = useNetworkConnection();

  const selectPreset = (index: number): void => {
    setNoteMessage(null);
    setselectedPreset(index);
    if (index === -1) {
      setSelectedAccessTypes(defaultAccessTypes);
      return;
    }
    const selectedPresetValue = presets[index];
    const moduleList: IModuleAccessObject = {};
    selectedPresetValue?.moduleList?.forEach((element) => {
      moduleList[element.moduleName] = element.accessType;
    });
    setSelectedAccessTypes((prvs) => {
      systemModules.forEach((module: any) => {
        moduleList[module.moduleName] = prvs[module.moduleName] ?? ModuleAccessLevel.NOACCESS;
      });
      return moduleList;
    });
  };

  const handleRadioButtonChange = useCallback(
    (moduleName: string, accessType: ModuleAccessLevel, isSystemModule?: boolean) => {
      setSelectedAccessTypes((prevAccessTypes) => {
        const newAccessTypes = {
          ...prevAccessTypes,
          [moduleName]: accessType
        };
        if (!isSystemModule) {
          setselectedPreset(-1);
        }
        return newAccessTypes;
      });
      setNoteMessage(null);
    },
    [] // Removed defaultAccessTypes dependency to prevent unnecessary re-creation
  );

  const isPartialAccessSelected = (moduleName: string) => {
    return selectedAccessTypes[moduleName] === ModuleAccessLevel.PARTIALACCESS;
  };

  const isFullAccessSelected = (moduleName: string) => {
    return selectedAccessTypes[moduleName] === ModuleAccessLevel.FULLACCESS;
  };

  const handleButtonClick = useCallback(() => {
    setShowLoader(false);
    setShowAlert(false);
    setShowForm(true);
  }, []);

  const switchTab = useCallback((tab: string) => {
    setActiveTab(tab);
  }, []);

  const submitModule = async (): Promise<void> => {
    setSubmissionInProgress(true);
    setShowLoader(true);

    const filteredAccessTypes = Object.keys(selectedAccessTypes)
      .filter((moduleName) => selectedAccessTypes[moduleName] !== ModuleAccessLevel.NOACCESS)
      .reduce<IModuleAccessObject>((obj, moduleName) => {
        obj[moduleName] = selectedAccessTypes[moduleName];
        return obj;
      }, {});

    const moduleArray = Object.entries(filteredAccessTypes).map(([moduleName, accessType]) => ({
      moduleName,
      accessType
    }));

    const presetName = selectedPreset !== -1 ? presets[selectedPreset].presetName : '';
    const BATCH_SIZE = 30;
    const batches = [];

    for (let i = 0; i < moduleArray.length; i += BATCH_SIZE) {
      batches.push(moduleArray.slice(i, i + BATCH_SIZE));
    }

    if (batches.length === 0) {
      batches.push([]);
    }

    const subPath = '/module/assigneModule';
    const axiosWithRoles = apiCallWrapper(subPath, 'POST');

    try {
      for (let i = 0; i < batches.length; i++) {
        const formattedData = {
          orgname: organisationName,
          moduleList: batches[i],
          presetName: i === 0 ? presetName || undefined : undefined,
          batchIndex: i,
          totalBatches: batches.length,
          isLastBatch: i === batches.length - 1
        };

        const response = await axiosWithRoles.post(
          `${backEndServerUrl}/module/assigneModule`,
          formattedData
        );

        if (response?.status !== 200 && response?.status !== 201 && response?.status !== 202) {
          throw new Error(`Batch ${i + 1} submission failed`);
        }

        // Wait 2 seconds between batches for backend to fully process
        if (i < batches.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 2000));
        }
      }

      // Wait for final batch to complete (longer for multiple batches)
      const finalWaitTime = batches.length > 1 ? batches.length * 1000 + 3000 : 3000;
      await new Promise((resolve) => setTimeout(resolve, finalWaitTime));

      // Retry verification up to 3 times to handle backend processing delays
      let verificationAttempts = 0;
      let refreshedPartner = null;

      while (verificationAttempts < 3) {
        const result = await dispatch(getAllAdmins());
        const updatedPartnersData = (result.payload as IUserList[]) || partnersState.data || [];
        refreshedPartner = updatedPartnersData.find((p: IUserList) => p.email === partner.email);

        if (refreshedPartner && refreshedPartner.moduleList?.length === moduleArray.length) {
          break;
        }

        verificationAttempts++;
        if (verificationAttempts < 3) {
          await new Promise((resolve) => setTimeout(resolve, 2000));
        }
      }

      if (refreshedPartner && refreshedPartner.moduleList?.length === moduleArray.length) {
        const updatedPartner = {
          ...partner,
          moduleList: moduleArray,
          module: moduleArray.length.toString(),
          presetName: presetName || undefined
        };

        onChangePartner(updatedPartner);
        setDefaultAccessTypes(selectedAccessTypes);
        setResponseMessage('Module Assigned Successfully!!');
        setNoteMessage(null);
      } else {
        setSelectedAccessTypes(defaultAccessTypes);
        setResponseMessage(
          `Failed to save all modules. Only ${refreshedPartner?.moduleList?.length ?? 0} of ${
            moduleArray.length
          } modules were saved. Please contact support.`
        );
      }
    } catch (error: any) {
      setResponseMessage(
        error?.response?.data?.message || 'Failed to assign modules. Please try again.'
      );
    } finally {
      setShowLoader(false);
      setShowAlert(true);
      setSubmissionInProgress(false);
    }
  };

  const popupCloseAlertHandler = (e: boolean) => {
    setShowAlert(e);
    setShowForm(e);
  };

  const closeModal = () => {
    setSelectedAccessTypes(defaultAccessTypes);
    setShowForm(false);
    setShowAlert(false);
    setShowConfirmation(false);
    if (partner.presetName) {
      const accessPreset = presets.findIndex(
        (preset) => preset.presetName.trim() === partner.presetName?.trim()
      );
      setselectedPreset(accessPreset);
    } else {
      setselectedPreset(-1);
    }
  };

  const openModal = () => {
    setselectedPreset(selectedPreset);
    setShowForm(true);
    setShowAlert(false);
    setShowConfirmation(false);
    setCanReopen(true);
  };

  const popupCloseHandler = useCallback(
    (e: boolean) => {
      if (!submissionInProgress) {
        let hasChanges = false;
        for (const moduleName in selectedAccessTypes) {
          const currentAccessType = selectedAccessTypes[moduleName];
          const defaultAccessType = defaultAccessTypes[moduleName];
          const isChangedFromDefault =
            defaultAccessType !== undefined
              ? currentAccessType !== defaultAccessType
              : currentAccessType !== ModuleAccessLevel.NOACCESS;
          if (isChangedFromDefault) {
            hasChanges = true;
            break;
          }
        }
        if (hasChanges) {
          setShowForm(e);
          setShowConfirmation(true);
          setCanReopen(true);
        } else {
          if (partner.presetName) {
            const accessPreset = presets.findIndex(
              (preset) => preset.presetName.trim() === partner.presetName?.trim()
            );
            setselectedPreset(accessPreset);
          }
          setShowForm(false);
        }
      } else {
        setShowForm(false); // If submission is in progress, close the form without showing the confirmation
      }
    },
    [defaultAccessTypes, selectedAccessTypes, submissionInProgress]
  );

  useEffect(() => {
    const getUpdatedSelectValue = (
      defaultAccess: IModuleAccessObject,
      currentPartner: IUserList
    ): IModuleAccessObject => {
      // Update selected preset on UI, if selected
      if (currentPartner.presetName) {
        const accessPreset = presetsRef.current.findIndex(
          (preset) => preset.presetName?.trim() === currentPartner.presetName?.trim()
        );
        if (accessPreset !== -1) {
          const selectedPresetValue = presetsRef.current[accessPreset];
          setselectedPreset(accessPreset);
          const presetAccess: IModuleAccessObject = {};
          const defaultbaseModuleAccess: IModuleAccessObject = {};
          const defaultsystemModuleAccess: IModuleAccessObject = {};
          const basePresetAccess: IModuleAccessObject = {};
          // Preset module access object.
          selectedPresetValue.moduleList.forEach((item) => {
            presetAccess[item.moduleName] = item.accessType;
          });
          baseModulesRef.current.forEach((module) => {
            // filter preset base modules access.
            basePresetAccess[module.moduleName] =
              presetAccess[module.moduleName] || ModuleAccessLevel.NOACCESS;
            // Filter base modules access.
            defaultbaseModuleAccess[module.moduleName] =
              defaultAccess[module.moduleName] || ModuleAccessLevel.NOACCESS;
          });
          systemModulesRef.current.forEach((module) => {
            // Filter system modules access.
            defaultsystemModuleAccess[module.moduleName] =
              defaultAccess[module.moduleName] || ModuleAccessLevel.NOACCESS;
          });

          // Compare if preset access changed.
          const isEqual = Object.entries(basePresetAccess).every(
            ([key, value]) => defaultbaseModuleAccess[key] === value
          );
          if (!isEqual) {
            setNoteMessage('Assigned preset access has been changed.');
          }
          return { ...basePresetAccess, ...defaultsystemModuleAccess };
        }
      }
      return defaultAccess;
    };
    const updateUi = () => {
      // Stores the existing access
      const defaultAccessModuleList: IModuleAccessObject = {};
      // Initialize all modules to NOACCESS first
      [...baseModulesRef.current, ...systemModulesRef.current].forEach((module) => {
        defaultAccessModuleList[module.moduleName] = ModuleAccessLevel.NOACCESS;
      });
      // Update Existing access of modules (defaultAccessTypes) and keep track in selectAccessModuleList
      if (partnerFromRedux?.moduleList?.length > 0) {
        partnerFromRedux.moduleList.forEach((selectedModule: any) => {
          const accessType = selectedModule.accessType;
          defaultAccessModuleList[selectedModule.moduleName] = accessType;
        });
      }
      setDefaultAccessTypes(defaultAccessModuleList);
      // Get initialy selected access.
      const selectAccessModuleList: IModuleAccessObject = getUpdatedSelectValue(
        defaultAccessModuleList,
        partnerFromRedux
      );
      setSelectedAccessTypes(selectAccessModuleList);
      setIsLoading(false);
    };
    if (!canReOpen) {
      void updateUi();
    } else {
      setCanReopen(false);
    }
  }, [showForm, partnerFromRedux]);

  return (
    <Fragment>
      {isConnected && (
        <div>
          <Button
            className="text-white bg-black rounded-md text-xs py-1 px-4 ml-2"
            buttonText="Access"
            onclick={handleButtonClick}
          />
          {showForm && (
            <CustomPopup
              onClose={popupCloseHandler}
              show={showForm}
              width={'w-full sm:min-w-[80%] md:max-w-fit lg:min-w-fit'}>
              <div className="flex start justify-between px-4 py-4 pt-[0rem]">
                <IPPFourthLevelHeading
                  headerText={'Assign Modules'}
                  className={'mb-0 leading-normal text-2xl text-black'}
                />
              </div>
              <br />
              <ConditionalLoader isLoading={isLoading} className="my-16" width={350}>
                <div className="max-w-full overflow-hidden overflow-x-scroll">
                  <div className="flex justify-start gap-2">
                    <div className="min-w-fit max-h-[34rem] border-r border-black">
                      <div className="w-full inline-block text-black rounded-t-lg lg:w-[18rem] py-4 px-4 text-[18px] text-center border-b border-black">
                        <p className="flex justify-between">
                          <span>Presets</span>{' '}
                          <span className="px-2 py-1 text-sm rounded-full border border-gray">
                            {presets.length}
                          </span>
                        </p>
                      </div>
                      <div className="max-h-[90%] overflow-hidden overflow-y-auto">
                        <div className="flex flex-col gap-2 items-center justify-center pt-2 overflow-hidden overflow-y-auto pr-2">
                          <Button
                            className={`text-left inline-block mb-0 h-[50px] text-[17px] w-full cursor-pointer rounded-md px-4 py-2 ${
                              selectedPreset === -1 && 'bg-gray text-black'
                            }`}
                            buttonText="Custom"
                            onclick={() => {
                              selectPreset(-1);
                            }}
                          />
                          {presets.map((preset, index) => (
                            <Button
                              key={preset.presetName + index}
                              className={`text-left inline-block mb-0 h-[50px] text-[17px] w-full cursor-pointer rounded-md px-4 py-2 ${
                                selectedPreset === index && 'bg-gray text-black'
                              }`}
                              buttonText={preset.presetName}
                              onclick={() => {
                                selectPreset(index);
                              }}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="flex-grow">
                      <div className="border-b border-black">
                        <div
                          className={
                            activeTab === 'Base Modules'
                              ? 'inline-block text-black rounded-t-lg lg:w-[18rem] py-4 px-4 text-[18px] text-center active bg-gray cursor-pointer'
                              : 'inline-block text-black rounded-t-lg lg:w-[18rem] py-4 px-4 text-[18px] text-center cursor-pointer'
                          }
                          onClick={() => switchTab('Base Modules')}>
                          Base Modules
                        </div>
                        <div
                          className={
                            activeTab === 'Custom Modules'
                              ? 'inline-block text-black rounded-t-lg lg:w-[18rem] py-4 px-4 text-[18px] text-center active bg-gray cursor-pointer'
                              : 'inline-block text-black rounded-t-lg lg:w-[18rem] py-4 px-4 text-[18px] text-center cursor-pointer'
                          }
                          onClick={() => switchTab('Custom Modules')}>
                          Custom Modules
                        </div>
                      </div>
                      <div>
                        {activeTab === 'Base Modules' && baseModules.length > 0 ? (
                          <div className="position-relative grow shrink basis-auto p-[1rem]block max-h-[30rem] overflow-y-auto">
                            <table className="w-full border-separate border-spacing-y-[3px]">
                              <thead className="table-header-group align-middle bg-[#f8f8f8] sticky-header">
                                <tr className="text-black text-left table-row align-inherit">
                                  <th className="font-semibold text-sm uppercase px-6 py-4 text-center">
                                    Module Name
                                  </th>
                                  <th className="font-semibold text-sm uppercase px-6 py-4 text-center">
                                    Full Access
                                  </th>
                                  <th className="font-semibold text-sm uppercase px-6 py-4 text-center">
                                    Partial Access
                                  </th>
                                  <th className="font-semibold text-sm uppercase px-6 py-4 text-center">
                                    No Access
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {baseModules
                                  .sort((a: { moduleName: string }, b: { moduleName: any }) =>
                                    a.moduleName.localeCompare(b.moduleName)
                                  )
                                  .map((element: IModuleList, index: number) => (
                                    <tr key={index}>
                                      <td
                                        className={`px-6 py-4 text-center ${
                                          (selectedAccessTypes[element.moduleName] !==
                                            ModuleAccessLevel.NOACCESS ||
                                            defaultAccessTypes[element.moduleName] !== undefined) &&
                                          selectedAccessTypes[element.moduleName] !==
                                            defaultAccessTypes[element.moduleName]
                                            ? 'bg-gray rounded-[50px]'
                                            : ''
                                        }`}>
                                        {element.moduleName}
                                      </td>
                                      <td className="px-6 py-4 text-center">
                                        <div className="flex items-center ml-[2.2rem]">
                                          <InputField
                                            id={`full-access-radio-${index}`}
                                            type="radio"
                                            name={element.moduleName}
                                            className={
                                              'w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600 accent-black'
                                            }
                                            checked={isFullAccessSelected(element.moduleName)}
                                            onChange={() =>
                                              handleRadioButtonChange(
                                                element.moduleName,
                                                ModuleAccessLevel.FULLACCESS
                                              )
                                            }
                                          />
                                        </div>
                                      </td>
                                      <td className="px-6 py-4 text-center">
                                        <div className="flex items-center ml-[3rem]">
                                          <InputField
                                            id={`partial-access-radio-${index}`}
                                            type="radio"
                                            name={element.moduleName}
                                            className={
                                              'w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600 accent-black'
                                            }
                                            checked={isPartialAccessSelected(element.moduleName)}
                                            onChange={() =>
                                              handleRadioButtonChange(
                                                element.moduleName,
                                                ModuleAccessLevel.PARTIALACCESS
                                              )
                                            }
                                          />
                                        </div>
                                      </td>
                                      <td className="px-6 py-4 text-center">
                                        <div className="flex items-center ml-[2rem]">
                                          <InputField
                                            id={`no-access-radio-${index}`}
                                            type="radio"
                                            name={element.moduleName}
                                            className={
                                              'w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600 accent-black'
                                            }
                                            checked={
                                              selectedAccessTypes[element.moduleName] ===
                                                ModuleAccessLevel.NOACCESS ||
                                              (!isPartialAccessSelected(element.moduleName) &&
                                                !isFullAccessSelected(element.moduleName))
                                            }
                                            onChange={() =>
                                              handleRadioButtonChange(
                                                element.moduleName,
                                                ModuleAccessLevel.NOACCESS
                                              )
                                            }
                                          />
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          systemModules.length === 0 && (
                            <div className="px-6 py-4 text-center pt-20 font-bold">
                              <IPPHeading
                                headerText={'No data found ☹️ !!'}
                                className={'text-bold'}
                              />
                            </div>
                          )
                        )}
                        {activeTab === 'Custom Modules' && systemModules.length > 0 ? (
                          <div className="position-relative grow shrink basis-auto p-[1rem]block max-h-[30rem] overflow-y-auto">
                            <table className="w-100% border-collapse">
                              <thead className="table-header-group align-middle bg-[#f8f8f8] sticky-header">
                                <tr className="text-black text-left table-row align-inherit">
                                  <th className="font-semibold text-sm uppercase px-6 py-4 text-center">
                                    Module Name
                                  </th>
                                  <th className="font-semibold text-sm uppercase px-6 py-4 text-center">
                                    Full Access
                                  </th>
                                  <th className="font-semibold text-sm uppercase px-6 py-4 text-center">
                                    Partial Access
                                  </th>
                                  <th className="font-semibold text-sm uppercase px-6 py-4 text-center">
                                    No Access
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {systemModules
                                  .sort((a: { moduleName: string }, b: { moduleName: any }) =>
                                    a.moduleName.localeCompare(b.moduleName)
                                  )
                                  .map((module: IModuleList, index: number) => (
                                    <tr key={index}>
                                      <div className="mt-[5px]">
                                        <td
                                          className={`px-10 py-4 text-center ${
                                            (selectedAccessTypes[module.moduleName] !==
                                              ModuleAccessLevel.NOACCESS ||
                                              defaultAccessTypes[module.moduleName] !==
                                                undefined) &&
                                            selectedAccessTypes[module.moduleName] !==
                                              defaultAccessTypes[module.moduleName]
                                              ? 'bg-gray rounded-[50px]'
                                              : ''
                                          }`}>
                                          {module.moduleName}
                                        </td>
                                      </div>
                                      <td className="px-6 py-4 text-center">
                                        <div className="flex items-center ml-[2.2rem]">
                                          <InputField
                                            id={`full-access-radio-${index}`}
                                            type="radio"
                                            name={module.moduleName}
                                            className={
                                              'w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600 accent-black'
                                            }
                                            checked={isFullAccessSelected(module.moduleName)}
                                            onChange={() =>
                                              handleRadioButtonChange(
                                                module.moduleName,
                                                ModuleAccessLevel.FULLACCESS,
                                                true
                                              )
                                            }
                                          />
                                        </div>
                                      </td>
                                      <td className="px-6 py-4 text-center">
                                        <div className="flex items-center ml-[3rem]">
                                          <InputField
                                            id={`partial-access-radio-${index}`}
                                            type="radio"
                                            name={module.moduleName}
                                            className={
                                              'w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600 accent-black'
                                            }
                                            checked={isPartialAccessSelected(module.moduleName)}
                                            onChange={() =>
                                              handleRadioButtonChange(
                                                module.moduleName,
                                                ModuleAccessLevel.PARTIALACCESS,
                                                true
                                              )
                                            }
                                          />
                                        </div>
                                      </td>
                                      <td className="px-6 py-4 text-center">
                                        <div className="flex items-center ml-[2rem]">
                                          <InputField
                                            id={`no-access-radio-${index}`}
                                            type="radio"
                                            name={module.moduleName}
                                            className={
                                              'w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600 accent-black'
                                            }
                                            checked={
                                              selectedAccessTypes[module.moduleName] ===
                                                ModuleAccessLevel.NOACCESS ||
                                              (!isPartialAccessSelected(module.moduleName) &&
                                                !isFullAccessSelected(module.moduleName))
                                            }
                                            onChange={() =>
                                              handleRadioButtonChange(
                                                module.moduleName,
                                                ModuleAccessLevel.NOACCESS,
                                                true
                                              )
                                            }
                                          />
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          baseModules.length === 0 && (
                            <div className="px-6 py-4 text-center pt-20 font-bold">
                              <IPPHeading
                                headerText={'No data found ☹️ !!'}
                                className={'text-bold'}
                              />
                            </div>
                          )
                        )}
                        {showAlert && (
                          <CustomPopup
                            onClose={popupCloseAlertHandler}
                            show={showAlert}
                            width={'w-41%'}>
                            <div className="max-h-30% ">
                              <IPPParagraph
                                headerText={`${responseMessage}`}
                                className={'mr-0 mb-0.5 ml-0 text-[17px]'}
                              />
                            </div>
                          </CustomPopup>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                {noteMessage && (
                  <p className="mt-2 py-2 text-red text-center bg-gradient-to-b from-transparent to-offwhite border-gray rounded-md">
                    Note: {noteMessage}
                  </p>
                )}
                <div className="bg-white p-4 pb-0 inset-x-0 mt-2">
                  <Button
                    className="inline-block mb-0 h-[42px] text-[#fff] text-[17px] w-[9rem] cursor-pointer bg-black rounded-md px-4 py-2"
                    buttonText="Submit"
                    onclick={submitModule}
                    loading={showLoader}
                    disabled={showLoader}
                  />
                </div>
              </ConditionalLoader>
            </CustomPopup>
          )}
          {showConfirmation && (
            <ConfirmationPopUp show={showConfirmation}>
              <div className="max-h-30% ">
                <IPPParagraph
                  headerText={'You have made changes. Do you want to discard?'}
                  className={'mr-0 mb-0.5 ml-0 text-[23px]'}
                />
                <br />
                <div className="flex justify-center">
                  <Button
                    className="h-10 px-4 py-[1.75] bg-white hover:bg-black hover:text-white text-[#2c323f] rounded focus:outline-none border border-spacing-1 border-black mr-3"
                    buttonText="Yes"
                    onclick={closeModal}
                  />
                  <Button
                    className="px-4 py-[1.75] bg-white hover:bg-black hover:text-white text-[#2c323f] rounded focus:outline-none border border-spacing-1 border-black ml-3"
                    buttonText="No"
                    onclick={openModal}
                  />
                </div>
              </div>
            </ConfirmationPopUp>
          )}
        </div>
      )}
    </Fragment>
  );
};

export default AssignedModuleForm;
